import React, { useCallback, useMemo, useState } from "react";
import { Spin, message } from "antd";
import dayjs from "dayjs";
import { serverFetch } from "@extensions";
import { useCompany, useUser } from "@redux";
import { recordPassVidOptions } from "../../extensions/utils";
import CatalogSaleDates from "./catalog-sale-dates";
import "./catalog-sale-groups.css";

const CatalogSaleGroups = ({ productId, onSelect, initialGroupRecord = null, initialRecordId = null, initialDate = null }) => {
  const company = useCompany();
  const user = useUser();
  const initialDateValue = initialDate || initialGroupRecord?.docDate || null;
  const initialSelectedDate = useMemo(() => initialDateValue ? dayjs(initialDateValue) : null, [initialDateValue]);
  const initialSelectedRecordId = initialRecordId || initialGroupRecord?.id || null;
  const initialGroups = useMemo(() => initialGroupRecord ? [initialGroupRecord] : [], [initialGroupRecord]);
  const [groups, setGroups] = useState(initialGroups);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [selectedRecordId, setSelectedRecordId] = useState(initialSelectedRecordId);

  const availableDates = useMemo(() => {
    const values = groups
      .filter((group) => group.docDate)
      .map((group) => dayjs(group.docDate).format("YYYY-MM-DD"));
    return [...new Set(values)];
  }, [groups]);

  const groupsBySelectedDate = useMemo(() => {
    if (!selectedDate) {
      return [];
    }
    return groups.filter((group) => group.docDate && dayjs(group.docDate).isSame(selectedDate, "day"));
  }, [groups, selectedDate]);

  const formatTime = (timeSpan) => {
    if (!timeSpan) {
      return "";
    }
    return timeSpan.length >= 5 ? timeSpan.substring(0, 5) : timeSpan;
  };

  const getGroupName = (group) => group.comment || group.productName || "Групповое занятие";

  const getAvailabilityText = (group) => {
    if (group.availableCount === 1) {
      return "Осталось последнее место";
    }
    if (group.availableCount === 2) {
      return "Остались последние пару мест";
    }
    return "";
  };

  // Получение текстовой метки для вида прохода по его значению
  const getPassVidLabel = (passVid) => {
    if (passVid == null) return null;
    const option = recordPassVidOptions.find(opt => opt.value === passVid);
    return option?.label || null;
  };

  const fetchGroups = useCallback(async (from, to) => {
    if (!company || !productId || !from || !to) {
      setGroups([]);
      return;
    }

    setLoading(true);
    if (!initialSelectedRecordId) {
      setSelectedRecordId(null);
      if (onSelect) {
        onSelect(null);
      }
    }

    try {
      const queryParams = {
        productId: productId,
        from: from.format("YYYY-MM-DD"),
        to: to.format("YYYY-MM-DD"),
      };
      const data = await serverFetch("Online/ProductGroups", { method: "GET", queryParams: queryParams }, user);
      const loadedGroups = data || [];
      const hasInitialGroup = initialGroupRecord && loadedGroups.some((group) => group.id === initialGroupRecord.id);
      const nextGroups = initialGroupRecord && !hasInitialGroup
        ? [initialGroupRecord, ...loadedGroups]
        : loadedGroups;
      const preselectedGroup = initialSelectedRecordId
        ? nextGroups.find((group) => group.id === initialSelectedRecordId)
        : null;
      setGroups(nextGroups);
      if (preselectedGroup) {
        setSelectedDate(dayjs(preselectedGroup.docDate));
        setSelectedRecordId(preselectedGroup.id);
      }
    } catch (error) {
      console.error("Ошибка получения групповых записей:", error);
      message.error(error?.UserMessage || "Не удалось загрузить групповые записи");
      setGroups(initialGroups);
    } finally {
      setLoading(false);
    }
  }, [company, initialGroupRecord, initialGroups, initialSelectedRecordId, onSelect, productId, user]);

  const handleDateSelect = useCallback(({ date }) => {
    setSelectedDate(date);
    const keepInitialSelection = initialSelectedRecordId && initialSelectedDate && date.isSame(initialSelectedDate, "day");
    if (!keepInitialSelection) {
      setSelectedRecordId(null);
      if (onSelect) {
        onSelect(null);
      }
    }
  }, [initialSelectedDate, initialSelectedRecordId, onSelect]);

  const handleGroupSelect = useCallback((group) => {
    setSelectedRecordId(group.id);
    if (onSelect) {
      onSelect(group);
    }
  }, [onSelect]);

  return (
    <div className="catalog-sale-groups">
      <CatalogSaleDates
        onSelect={handleDateSelect}
        availableDates={availableDates}
        onlyAvailable={true}
        onWeekChange={fetchGroups}
        initialDate={initialSelectedDate}
      />
      {loading && groups.length === 0 ? (
        <div className="catalog-sale-groups-loading">
          <Spin size="small" />
        </div>
      ) : (
        <>
          {selectedDate && groupsBySelectedDate.length > 0 && (
            <div className="catalog-sale-groups-list-wrapper">
              <div className="catalog-sale-groups-title">Выберите группу</div>
              <div className="catalog-sale-groups-list">
                {groupsBySelectedDate.map((group) => (
                    <div
                      key={group.id}
                      className={`catalog-sale-groups-card ${selectedRecordId === group.id ? "selected" : ""}`}
                      onClick={() => handleGroupSelect(group)}
                    >
                      <div className="catalog-sale-groups-main">
                        <div className="catalog-sale-groups-name">{getGroupName(group)}</div>
                        <div className="catalog-sale-groups-time">
                          {formatTime(group.start)} - {formatTime(group.finish)}
                        </div>
                        <div className="catalog-sale-groups-details">
                          {group.roomName && <span>{group.roomName}</span>}
                          {group.userName && <span>{group.userName}</span>}
                        </div>
                        {getPassVidLabel(group.passVid) && (
                          <div className="catalog-sale-groups-pass-hint" style={{ fontSize: 13, fontWeight: 1000, marginTop: 4 }}>
                            Запись производится {getPassVidLabel(group.passVid)}
                          </div>
                        )}
                        {getAvailabilityText(group) && (
                          <div className="catalog-sale-groups-availability">
                            {getAvailabilityText(group)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
          {selectedDate && groupsBySelectedDate.length === 0 && (
            <div className="catalog-sale-groups-empty">На выбранную дату нет доступных групп</div>
          )}
        </>
      )}
    </div>
  );
};

export default CatalogSaleGroups;